import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { apiFetch } from '../lib/api.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import HostHeader from '../components/ui/HostHeader.jsx'
import HostSideNav from '../components/ui/HostSideNav.jsx'
import { pageBg as podiumBg } from '../lib/images'
import { GAME_DEFAULTS } from '../lib/constants.js'
import * as musicPlayer from '../lib/music.js'

const POLL_INTERVAL = 2000

export default function HostPage() {
  const { code } = useParams()
  const navigate  = useNavigate()

  const [game, setGame]         = useState(null)
  const [players, setPlayers]   = useState([])
  const [maxPlayers, setMaxPlayers] = useState(GAME_DEFAULTS.MAX_PLAYERS)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState('')
  const [startLoading, setStartLoading] = useState(false)
  const [musicEnabled, setMusicEnabled] = useState(true)

  const pollingRef = useRef(null)
  const joinUrl    = `${window.location.origin}/join/${code}`

  // Chargement initial de la partie — redirige vers stats si déjà terminée
  useEffect(() => {
    apiFetch(`/api/games/${code}`)
      .then(data => {
        if (data?.statut === 'finished') {
          navigate(`/stats/${code}`, { replace: true })
          return
        }
        setGame(data)
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [code])

  // Polling des joueurs toutes les 2s
  useEffect(() => {
    async function fetchPlayers() {
      try {
        const { players: ps, max_joueurs } = await apiFetch(`/api/games/${code}/players`)
        setPlayers(ps)
        setMaxPlayers(max_joueurs)
      } catch (_) {}
    }
    fetchPlayers()
    pollingRef.current = setInterval(fetchPlayers, POLL_INTERVAL)
    return () => clearInterval(pollingRef.current)
  }, [code])

  async function handleStart() {
    if (startLoading || players.length === 0) return
    setStartLoading(true)
    const musicUrl = game?.decks?.musiques?.url ?? null
    if (musicUrl && musicEnabled) {
      musicPlayer.initAudio(musicUrl, true)
      musicPlayer.play()
    }
    try {
      const data = await apiFetch(`/api/games/${code}/start`, { method: 'PUT' })
      handleGameUpdate('start', data)
    } catch (e) {
      musicPlayer.stop()
      setError(e.message)
    }
    setStartLoading(false)
  }

  // Callback des actions du header
  function handleGameUpdate(type, data) {
    if (type === 'start') { navigate(`/race/${code}`, { replace: true }); return }
    if (type === 'end')   { navigate(`/podium/${code}`, { replace: true }); return }
    if (data?.statut)     setGame(g => ({ ...g, statut: data.statut }))
  }

  if (loading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-gray-900">
        <div className="text-white/40">Chargement…</div>
      </div>
    )
  }

  const statut    = game?.statut ?? 'waiting'
  const deckTitle = game?.decks?.titre ?? '—'

  const bgStyle = { backgroundImage: `url(${podiumBg})` }

  return (
    <div className="h-[100dvh] flex flex-col bg-gray-900">

      <HostHeader
        code={code}
        deckTitle={deckTitle}
        statut={statut}
        playerCount={players.length}
        maxPlayers={maxPlayers}
        startDisabled={players.length === 0}
        onGameUpdate={handleGameUpdate}
      />

      <div className="flex flex-1 min-h-0 bg-cover bg-center sm:bg-top bg-no-repeat" style={bgStyle}>
        <HostSideNav
          code={code}
          active="join"
          statut={statut}
          deckTitle={deckTitle}
          onGameUpdate={handleGameUpdate}
          startDisabled={players.length === 0}
        />

        {/* Contenu */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 sm:pb-6">
          <div className="flex flex-col items-center justify-center min-h-full">
          {error && (
            <div className="bg-red-500/20 text-red-200 border border-red-500/30 rounded-lg px-4 py-2 mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="max-w-4xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* QR Code + lien */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex flex-col items-center text-center gap-4">
              <h2 className="text-xs font-semibold text-white uppercase tracking-widest self-start">
                Rejoindre la partie
              </h2>

              <div className="bg-white p-4 rounded-2xl shadow-lg">
                <QRCodeSVG value={joinUrl} size={180} level="M" includeMargin={false} />
              </div>

              <div>
                <p className="text-white text-xs mb-1">Code de la partie</p>
                <div className="font-mono text-4xl font-semibold tracking-[0.25em] text-white bg-white/10 px-6 py-3 rounded-xl border border-white/20">
                  {code}
                </div>
              </div>

              <div className="w-full">
                <p className="text-white text-xs mb-1">Ou via ce lien</p>
                <div className="bg-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono border border-white/10 flex items-center gap-2">
                  <span className="break-all flex-1">{joinUrl}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(joinUrl)}
                    title="Copier le lien"
                    className="shrink-0 text-white/50 hover:text-white transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
              </div>

              {statut === 'waiting' && game?.decks?.musiques?.url && (
                <div className="w-full flex items-center justify-between px-1">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <span>{musicEnabled ? '🔊' : '🔇'}</span>
                    <span>{musicEnabled ? 'Musique activée' : 'Musique désactivée'}</span>
                    <span className="text-white/40 text-xs truncate max-w-[120px]">{game.decks.musiques.titre}</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={musicEnabled}
                    onClick={() => setMusicEnabled(v => !v)}
                    className={`relative inline-flex h-6 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      musicEnabled ? 'bg-blue-500' : 'bg-white/20'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                      musicEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              )}

              {statut === 'waiting' && (
                <button
                  onClick={handleStart}
                  disabled={startLoading || players.length === 0}
                  className="w-full bg-green-500 hover:bg-green-400 disabled:opacity-40 text-white font-semibold text-sm py-3 rounded-xl transition"
                >
                  {startLoading ? '…' : '▶ Lancer la partie'}
                </button>
              )}
            </div>

            {/* Grille des joueurs */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between shrink-0">
                <h2 className="text-xs font-semibold text-white uppercase tracking-widest">
                  Joueurs connectés
                </h2>
                <span className="text-sm font-semibold text-white">
                  {players.length}
                  <span className="text-white/40 font-normal">/{maxPlayers}</span>
                </span>
              </div>

              {players.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-2">
                  <div className="text-4xl">👀</div>
                  <p className="text-sm font-medium text-white">En attente des joueurs…</p>
                  <p className="text-xs text-white">Partage le code ou le QR code.</p>
                </div>
              ) : (
                <div className="flex-1 grid grid-cols-4 gap-x-6 gap-y-5 overflow-y-auto py-1 justify-items-center content-start">
                  {players.map(p => (
                    <div key={p.id} className="flex flex-col items-center gap-1.5">
                      <TwemojiImg emoji={p.type_vehicule} size={56} />
                      <span className="text-sm font-semibold text-white text-center leading-tight break-all max-w-full">
                        {p.pseudo}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
          </div>
        </main>
      </div>
    </div>
  )
}
